/**
 * Design tokens for the redesigned shell and the three pages that live inside it
 * (front page, game board, standings).
 *
 * These are deliberately separate from the older palette in `index.css` / the legacy
 * components: the redesign steps its backgrounds (page -> shell -> panel -> card) where
 * the old one used one flat `#0f172a` plus borders. Mixing the two reads as a bug, so a
 * component belongs to one system or the other, not both.
 *
 * House rules baked in here:
 *   - `muted` (#94a3b8) is the FLOOR for any label. Anything dimmer failed contrast in
 *     design review. `dim`/`faint`/`ghost` are for rules, separators and decoration only.
 *   - No radii except circles (crests, avatars, status dots, seed badges).
 *   - No shadows. Depth comes from the background steps and 1px borders.
 */

import type { CSSProperties } from 'react'

export const BG = {
  page: '#070c15',
  shell: '#0b1220',
  panel: '#0f172a',
  card: '#131e2f',
  cardOwn: '#17222f',
  plateHover: '#1b2739',
} as const

export const BORDER = {
  hairline: '#1e293b',
  subtle: '#16202f',
  raised: '#334155',
  hover: '#475569',
  aliveOutsideCut: '#26344a',
  eliminated: '#5b2b2f',
} as const

export const TEXT = {
  primary: '#f8fafc',
  strong: '#f1f5f9',
  body: '#e2e8f0',
  secondary: '#cbd5e1',
  /** The floor for anything a user has to read. */
  muted: '#94a3b8',
  dim: '#64748b',
  faint: '#475569',
  ghost: '#3f4c60',
} as const

export const ACCENT = {
  live: '#4ade80',
  success: '#22c55e',
  info: '#38bdf8',
  warning: '#f59e0b',
  negative: '#f87171',
  anomaly: '#c084fc',
  rules: '#2dd4bf',
  cards: '#c4b5fd',
  ownTeam: '#f472b6',
  upset: '#f97316',
  featured: '#a78bfa',
} as const

/** Muted, not neon — full-saturation playoff colors read as too loud in review. */
export const PLAYOFF = {
  topSeedRing: '#a87c33',
  topSeedText: '#e3b767',
  topSeedFill: 'rgba(200,150,63,0.16)',
  divisionRing: '#4b7d5c',
  divisionText: '#83c294',
  divisionFill: 'rgba(92,158,111,0.16)',
  wildcardRing: '#4a6e94',
  wildcardText: '#93b6de',
  wildcardFill: 'rgba(91,135,184,0.16)',
  cutline: '#c8963f',
  cutlineText: '#d9a94f',
} as const

export const MOMENTUM = { high: '#f97316', mid: '#fb923c', low: '#fdba74' } as const

export const FONT = "'pressStart', ui-monospace, monospace"

/** Every number that can change is tabular and lives in a fixed-width container. */
export const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const

/**
 * Height the fixed footer bar occupies.
 *
 * MOBILE ONLY now. The desktop shell dropped the footer and moved the version
 * badge to the foot of the nav, so nothing on desktop reserves this any more.
 */
export const FOOTER_HEIGHT = 40

/**
 * Below this the shell switches to its phone layout: the nav becomes a drawer and
 * every page stacks. 900 rather than 768 because the three-column pages (front page,
 * game page) need more than a phone's width before their rails are worth showing.
 */
export const SHELL_MOBILE_MAX = 900

export const NAV_WIDTH = 196
export const RAIL_WIDTH = 330

/**
 * Shorthand for the `font:` values the handoffs are written in, so a component can say
 * `font(800, 13, 1, '0.02em')` instead of repeating the family five times.
 */
export const font = (
  weight: number,
  size: number,
  lineHeight: number | string = 1,
  letterSpacing?: string,
): CSSProperties => ({
  fontFamily: FONT,
  fontWeight: weight,
  fontSize: `${size}px`,
  lineHeight: typeof lineHeight === 'number' ? lineHeight : lineHeight,
  ...(letterSpacing ? { letterSpacing } : {}),
})

/**
 * An AWAKENED player's name: blue, and lit.
 *
 * ⚠️ ONE definition, imported everywhere a player is listed. The glow means the same
 * thing on every surface, so a leader board that lights a name while the stats table
 * leaves it plain reads as a bug in whichever one the reader trusts less. It started
 * as an inline literal in PlayerLeaders and was about to become a third copy.
 *
 * ⚠️ `filter: drop-shadow`, NOT `text-shadow`, and that is the whole fix. Every name
 * cell sets `overflow: hidden` to get `text-overflow: ellipsis`, and an element's
 * overflow clips its own text-shadow at the padding box — measured on the front page,
 * an `<a>` 98x13px holding a 34px halo, so the glow was sliced off square on all four
 * sides and read as a blue rectangle rather than light. A filter is applied AFTER that
 * clip and paints outside the border box, so the bloom escapes with no padding, no
 * negative-margin compensation, and no change to the link's click target. Padding was
 * tried first and worked visually, but it grew the anchor by 20px vertically and had it
 * overlapping the rows above and below.
 *
 * drop-shadow also follows the ALPHA of the glyphs rather than boxing them, which is
 * what makes it read as light coming off the letters.
 *
 * Tuned to the TEXT: layers out to 18px on 13px type, none of them dense. The original
 * had a 0.95-alpha core at 3px, which closed the gaps BETWEEN letters and welded them
 * into a lit slab. Light has to start soft to read as light.
 */
export const AWAKENED_NAME = {
  color: '#60a5fa',
  fontWeight: 600,
  filter: [
    'drop-shadow(0 0 4px rgba(96,165,250,0.55))',
    'drop-shadow(0 0 10px rgba(96,165,250,0.30))',
    'drop-shadow(0 0 18px rgba(96,165,250,0.14))',
  ].join(' '),
} as const
